if (!customElements.get("buy-together")) {
  class BuyTogether extends HTMLElement {
    constructor() {
      super();
      this.items = [];
      this.totalNode = null;
      this.originalNode = null;
      this.savingsNode = null;
      this.savingsWrap = null;
      this.transferDiscountAmountNode = null;
      this.addButton = null;
      this.addLabel = null;
      this.addingNode = null;
      this.addedNode = null;
      this.summaryItems = [];
      this.addState = "idle";
      this.addedTimer = null;
      this.cartDrawer = null;
      this.cartUpdateEvent =
        (window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) ||
        "cart-update";
      this.cartErrorEvent =
        (window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartError) ||
        "cart-error";

      this.onItemClick = this.onItemClick.bind(this);
      this.onItemKeydown = this.onItemKeydown.bind(this);
      this.onVariantChange = this.onVariantChange.bind(this);
      this.onVariantOptionClick = this.onVariantOptionClick.bind(this);
      this.onOptionGroupKeydown = this.onOptionGroupKeydown.bind(this);
      this.onAdd = this.onAdd.bind(this);
      this.onSummaryClick = this.onSummaryClick.bind(this);
      this.onSummaryKeydown = this.onSummaryKeydown.bind(this);
    }

    connectedCallback() {
      this.items = Array.from(this.querySelectorAll("[data-buy-together-item]"));
      this.summaryItems = Array.from(
        this.querySelectorAll("[data-buy-together-summary-item]")
      );
      this.totalNode = this.querySelector("[data-buy-together-total]");
      this.originalNode = this.querySelector("[data-buy-together-original]");
      this.savingsNode = this.querySelector("[data-buy-together-savings]");
      this.savingsWrap = this.querySelector("[data-buy-together-savings-wrap]");
      this.transferDiscountAmountNode = this.querySelector(
        "[data-buy-together-transfer-discount] [data-transfer-discount-amount]"
      );
      this.transferDiscountPercentage = parseFloat(
        this.dataset.transferDiscountPercentage || "0"
      );
      this.addButton = this.querySelector("[data-buy-together-add]");
      this.addLabel = this.querySelector("[data-buy-together-add-label]");
      this.addingNode = this.querySelector("[data-buy-together-state-adding]");
      this.addedNode = this.querySelector("[data-buy-together-state-added]");

      this.items.forEach((item) => {
        const isMain = item.dataset.isMainProduct === "true";
        const isAvailable = item.dataset.available === "true";
        this.setSelected(item, isMain ? true : isAvailable);
        item.addEventListener("click", this.onItemClick);
        item.addEventListener("keydown", this.onItemKeydown);

        const variantSelect = item.querySelector("[data-buy-together-variant]");
        if (variantSelect) {
          variantSelect.addEventListener("change", this.onVariantChange);
        }

        const variantOptions = item.querySelectorAll("[data-buy-together-option]");
        variantOptions.forEach((optionButton) => {
          optionButton.addEventListener("click", this.onVariantOptionClick);
        });
      });

      this.optionGroups = Array.from(
        this.querySelectorAll("[data-buy-together-variant-kind]")
      );
      this.optionGroups.forEach((group) => {
        group.addEventListener("keydown", this.onOptionGroupKeydown);
      });

      this.summaryItems.forEach((summaryItem) => {
        summaryItem.addEventListener("click", this.onSummaryClick);
        summaryItem.addEventListener("keydown", this.onSummaryKeydown);
      });

      if (this.addButton) {
        this.addButton.addEventListener("click", this.onAdd);
      }

      this.setButtonState("idle");
      this.updateTotal();
    }

    disconnectedCallback() {
      this.items.forEach((item) => {
        item.removeEventListener("click", this.onItemClick);
        item.removeEventListener("keydown", this.onItemKeydown);

        const variantSelect = item.querySelector("[data-buy-together-variant]");
        if (variantSelect) {
          variantSelect.removeEventListener("change", this.onVariantChange);
        }

        const variantOptions = item.querySelectorAll("[data-buy-together-option]");
        variantOptions.forEach((optionButton) => {
          optionButton.removeEventListener("click", this.onVariantOptionClick);
        });
      });

      if (this.optionGroups) {
        this.optionGroups.forEach((group) => {
          group.removeEventListener("keydown", this.onOptionGroupKeydown);
        });
      }

      this.summaryItems.forEach((summaryItem) => {
        summaryItem.removeEventListener("click", this.onSummaryClick);
        summaryItem.removeEventListener("keydown", this.onSummaryKeydown);
      });

      if (this.addButton) {
        this.addButton.removeEventListener("click", this.onAdd);
      }

      if (this.addedTimer) {
        clearTimeout(this.addedTimer);
      }
    }

    formatMoney(cents) {
      return (
        window.webvista?.formatPriceAmount(
          cents,
          window.Shopify?.currency?.active
        ) || ""
      );
    }

    formatTransferPrice(cents) {
      const units = Math.round(
        (cents * (100 - this.transferDiscountPercentage)) / 10000
      );
      return this.formatMoney(units * 100);
    }

    updateItemPriceNodes(item, price, original) {
      const priceNode = item.querySelector("[data-buy-together-price]");
      const originalPriceNode = item.querySelector(
        "[data-buy-together-original-price]"
      );
      if (priceNode) {
        priceNode.textContent = this.formatMoney(price);
      }
      if (originalPriceNode) {
        originalPriceNode.textContent = this.formatMoney(original);
        originalPriceNode.classList.toggle("hidden", original <= price);
      }
      const itemTransferDiscountAmount = item.querySelector(
        "[data-buy-together-item-transfer-discount] [data-transfer-discount-amount]"
      );
      if (itemTransferDiscountAmount) {
        itemTransferDiscountAmount.textContent = this.formatTransferPrice(price);
      }
    }

    getSelectedItems() {
      return this.items.filter((item) => item.dataset.selected === "true");
    }

    hasUnavailableSelectedItems() {
      return this.getSelectedItems().some(
        (item) => item.dataset.available !== "true"
      );
    }

    findItemByProductId(productId) {
      return this.items.find((item) => item.dataset.productId === productId);
    }

    findSummaryByProductId(productId) {
      return this.summaryItems.find(
        (summary) => summary.dataset.productId === productId
      );
    }

    setButtonState(state) {
      this.addState = state;

      if (!this.addLabel || !this.addingNode || !this.addedNode) return;

      this.addLabel.classList.toggle("hidden", state !== "idle");
      this.addingNode.classList.toggle("hidden", state !== "adding");
      this.addingNode.classList.toggle("flex", state === "adding");
      this.addedNode.classList.toggle("hidden", state !== "added");
      this.addedNode.classList.toggle("flex", state === "added");

      if (this.addButton) {
        this.addButton.setAttribute(
          "aria-busy",
          state === "adding" ? "true" : "false"
        );
        const shouldDisable =
          state === "adding" ||
          this.getSelectedItems().length === 0 ||
          this.hasUnavailableSelectedItems();
        this.addButton.toggleAttribute("disabled", shouldDisable);
      }
    }

    getSectionsToRender() {
      const drawer = document.getElementById("Cart-Drawer");
      const drawerSectionId = drawer?.dataset?.sectionId || "cart-drawer";
      return [
        {
          id: "Cart-Icon-Bubble",
          section: "cart-icon-bubble",
          selector: ".shopify-section",
        },
        {
          id: "Cart-Drawer",
          section: drawerSectionId,
          selector: "[data-cart-drawer-content]",
        },
      ];
    }

    renderSections(sections = {}) {
      this.getSectionsToRender().forEach((section) => {
        const html = sections[section.section];
        if (!html) return;

        const container = document.getElementById(section.id);
        if (!container) return;

        if (!section.selector) {
          container.innerHTML = html;
          return;
        }

        const parsed = new DOMParser().parseFromString(html, "text/html");
        const element = parsed.querySelector(section.selector);
        if (element) {
          if (section.id === "Cart-Drawer") {
            const contentTarget = container.querySelector(
              "[data-cart-drawer-content]"
            );
            const summaryTarget = container.querySelector(
              "[data-cart-drawer-summary]"
            );
            const progressTarget = container.querySelector(
              "free-shipping-progress"
            );

            if (contentTarget) {
              contentTarget.innerHTML = element.innerHTML;
              if (window.webvista?.initLazyImageAnimation) {
                window.webvista.initLazyImageAnimation(contentTarget);
              }
            }

            const summaryElement = parsed.querySelector(
              "[data-cart-drawer-summary]"
            );
            if (summaryTarget && summaryElement) {
              summaryTarget.innerHTML = summaryElement.innerHTML;
              summaryTarget.className = summaryElement.className;
            }

            const progressSource = parsed.querySelector(
              "free-shipping-progress"
            );
            if (progressTarget && progressSource) {
              progressTarget.dataset.totalAmount =
                progressSource.dataset.totalAmount || "";
            }

            if (typeof container.refreshUpsell === "function") {
              container.refreshUpsell();
            }
            return;
          }

          container.innerHTML = element.innerHTML;
          return;
        }

        container.innerHTML = html;
      });
    }

    async openCartDrawer(opener) {
      const header = document.querySelector("header-component");
      const cartType = header?.dataset.cartType || "drawer";
      const cartOpenOnAdd = header?.dataset.cartOpenOnAdd !== "false";
      if (!cartOpenOnAdd) {
        if (window.location.pathname === (window.routes?.cart_url || "/cart")) {
          window.location.reload();
        }
        return;
      }
      if (cartType === "page") {
        const cartUrl = window.routes?.cart_url || "/cart";
        if (window.location.pathname === cartUrl) {
          window.location.reload();
        } else {
          window.location.href = cartUrl;
        }
        return;
      }
      if (!this.cartDrawer) {
        this.cartDrawer = document.getElementById("Cart-Drawer");
      }
      if (!this.cartDrawer) return;
      if (typeof this.cartDrawer.show === "function") {
        this.cartDrawer.show(opener);
        return;
      }
      await customElements.whenDefined("cart-drawer");
      if (typeof this.cartDrawer.show === "function") {
        this.cartDrawer.show(opener);
      }
    }

    updateTotal() {
      const selected = this.getSelectedItems();

      const totals = selected.reduce(
        (sum, item) => {
          const price = Number(item.dataset.price || 0);
          const original = Number(item.dataset.originalPrice || price);
          return {
            total: sum.total + price,
            original: sum.original + original,
          };
        },
        { total: 0, original: 0 }
      );

      if (this.totalNode) {
        this.totalNode.textContent = this.formatMoney(totals.total);
      }

      if (this.transferDiscountAmountNode) {
        this.transferDiscountAmountNode.textContent = this.formatTransferPrice(
          totals.total
        );
      }

      if (this.originalNode) {
        this.originalNode.textContent = this.formatMoney(totals.original);
        this.originalNode.classList.toggle("hidden", totals.original <= totals.total);
      }

      const savings = Math.max(totals.original - totals.total, 0);
      if (this.savingsNode) {
        this.savingsNode.textContent = this.formatMoney(savings);
      }
      if (this.savingsWrap) {
        this.savingsWrap.classList.toggle("hidden", savings <= 0);
      }

      if (this.addLabel && this.addButton) {
        if (this.hasUnavailableSelectedItems()) {
          this.addLabel.textContent =
            this.addButton.dataset.labelOutOfStock || "Out of Stock";
        } else {
          const tpl = this.addButton.dataset.labelAdd || "Add {{ count }} Items";
          this.addLabel.textContent = tpl.replace(
            "{{ count }}",
            String(selected.length)
          );
        }
      }

      if (this.addButton && this.addState !== "adding") {
        this.addButton.toggleAttribute(
          "disabled",
          selected.length === 0 || this.hasUnavailableSelectedItems()
        );
      }
    }

    updateItemImage(item, imageUrl) {
      const image = item.querySelector(".buy-together__image");
      if (!image || image.tagName !== "IMG") return;
      image.src = imageUrl || item.dataset.defaultImage || image.src;
    }

    setSelected(item, selected) {
      item.dataset.selected = selected ? "true" : "false";
      item.setAttribute("aria-checked", selected ? "true" : "false");

      const media = item.querySelector("[data-buy-together-card]");
      const checkWrap = item.querySelector("[data-buy-together-check-wrap]");
      const checkIcon = item.querySelector("[data-buy-together-check]");
      const title = item.querySelector("[data-buy-together-title]");
      const originalPrice = item.querySelector("[data-buy-together-original-price]");
      const price = item.querySelector("[data-buy-together-price]");

      if (media) {
        media.classList.toggle("border-accent", selected);
        media.classList.toggle("border-transparent", !selected);
        media.classList.toggle("opacity-100", selected);
        media.classList.toggle("opacity-60", !selected);
      }

      if (checkWrap) {
        checkWrap.classList.toggle("bg-accent", selected);
        checkWrap.classList.toggle("border-accent", selected);
        checkWrap.classList.toggle("bg-white", !selected);
        checkWrap.classList.toggle("border-white/20", !selected);
      }

      if (checkIcon) {
        checkIcon.classList.toggle("opacity-100", selected);
        checkIcon.classList.toggle("opacity-0", !selected);
      }

      if (title) {
        title.classList.toggle("text-foreground", selected);
        title.classList.toggle("text-subtitle", !selected);
      }

      if (price) {
        price.classList.toggle("text-foreground", selected);
        price.classList.toggle("text-foreground/40", !selected);
      }

      if (originalPrice) {
        originalPrice.classList.toggle("text-subtitle", selected);
        originalPrice.classList.toggle("text-foreground/40", !selected);
      }

      const variantWrap = item.querySelector("[data-buy-together-variant-wrap]");
      if (variantWrap) {
        variantWrap.classList.toggle("opacity-100", selected);
        variantWrap.classList.toggle("opacity-50", !selected);
      }

      this.updateVariantOptionStyles(item);

      const summary = this.findSummaryByProductId(item.dataset.productId);
      if (summary) {
        summary.setAttribute("aria-checked", selected ? "true" : "false");

        const summaryContent = summary.querySelector("[data-buy-together-summary-content]");
        const summaryCheckWrap = summary.querySelector(
          "[data-buy-together-summary-check-wrap]"
        );
        const summaryCheck = summary.querySelector("[data-buy-together-summary-check]");

        if (summaryContent) {
          summaryContent.classList.toggle("opacity-100", selected);
          summaryContent.classList.toggle("opacity-50", !selected);
        }

        if (summaryCheckWrap) {
          summaryCheckWrap.classList.toggle("bg-foreground", selected);
          summaryCheckWrap.classList.toggle("border-foreground", selected);
          summaryCheckWrap.classList.toggle("bg-background", !selected);
          summaryCheckWrap.classList.toggle("border-foreground/20", !selected);
        }

        if (summaryCheck) {
          summaryCheck.classList.toggle("scale-100", selected);
          summaryCheck.classList.toggle("scale-0", !selected);
        }
      }
    }

    updateVariantOptionStyles(item) {
      const selectedVariantId = item.dataset.variantId;
      const isItemSelected = item.dataset.selected === "true";
      const optionButtons = item.querySelectorAll("[data-buy-together-option]");

      optionButtons.forEach((button) => {
        const isActive = button.dataset.variantId === selectedVariantId;
        const optionKind = button.dataset.optionKind;

        if (optionKind === "color") {
          button.classList.toggle("ring-2", isActive && isItemSelected);
          button.classList.toggle("ring-offset-2", isActive && isItemSelected);
          button.classList.toggle("ring-foreground", isActive && isItemSelected);
        }

        if (optionKind === "size") {
          button.classList.toggle("bg-foreground", isActive);
          button.classList.toggle("text-background", isActive);
          button.classList.toggle("shadow-md", isActive);
          button.classList.toggle("text-foreground/50", !isActive);
          button.classList.toggle("hover:text-foreground/70", !isActive);
        }
      });
    }

    onOptionGroupKeydown(event) {
      if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;

      const group = event.currentTarget;
      const buttons = Array.from(
        group.querySelectorAll("[data-buy-together-option]:not([disabled])")
      );
      if (buttons.length < 2) return;

      const currentIndex = buttons.indexOf(document.activeElement);
      if (currentIndex === -1) return;

      event.preventDefault();

      const dir = event.key === "ArrowRight" ? 1 : -1;
      const nextIndex = (currentIndex + dir + buttons.length) % buttons.length;
      const nextButton = buttons[nextIndex];

      nextButton.focus();
      nextButton.click();
    }

    onItemKeydown(event) {
      if (event.key !== "Enter" && event.key !== " ") return;
      if (
        event.target.closest("[data-buy-together-option]") ||
        event.target.closest("[data-buy-together-variant]")
      ) return;
      event.preventDefault();
      this.onItemClick(event);
    }

    onItemClick(event) {
      if (
        event.target.closest("[data-buy-together-variant]") ||
        event.target.closest("[data-buy-together-option]")
      ) {
        return;
      }

      const item = event.currentTarget;
      if (!item) return;
      if (item.dataset.isMainProduct === "true") return;

      const isSelected = item.dataset.selected === "true";
      this.setSelected(item, !isSelected);
      this.updateTotal();
    }

    onSummaryKeydown(event) {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        this.onSummaryClick(event);
      }
    }

    onSummaryClick(event) {
      const summaryItem = event.currentTarget;
      if (!summaryItem) return;

      const item = this.findItemByProductId(summaryItem.dataset.productId);
      if (!item) return;
      if (item.dataset.isMainProduct === "true") return;

      const isSelected = item.dataset.selected === "true";
      this.setSelected(item, !isSelected);
      this.updateTotal();
    }

    onVariantChange(event) {
      const select = event.currentTarget;
      if (!select) return;

      const item = select.closest("[data-buy-together-item]");
      if (!item) return;

      const option = select.selectedOptions[0];
      if (!option) return;

      const price = Number(option.dataset.price || 0);
      const original = Number(option.dataset.originalPrice || price);
      const available = option.dataset.available === "true";

      item.dataset.price = String(price);
      item.dataset.originalPrice = String(original);
      item.dataset.variantId = option.value;
      item.dataset.available = available ? "true" : "false";

      this.updateItemImage(item, option.dataset.image);

      this.updateItemPriceNodes(item, price, original);

      if (
        !available &&
        item.dataset.selected === "true" &&
        item.dataset.isMainProduct !== "true"
      ) {
        this.setSelected(item, false);
      } else {
        this.updateVariantOptionStyles(item);
      }

      const summary = this.findSummaryByProductId(item.dataset.productId);
      if (summary) {
        const summaryVariant = summary.querySelector(
          "[data-buy-together-summary-variant]"
        );
        if (summaryVariant) {
          const variantText = option.textContent.trim();
          summaryVariant.textContent = variantText === "Default Title" ? "" : variantText;
          summaryVariant.classList.toggle("hidden", variantText === "Default Title");
        }
      }

      this.updateTotal();
    }

    onVariantOptionClick(event) {
      event.preventDefault();
      event.stopPropagation();

      const button = event.currentTarget;
      if (!button) return;
      if (button.disabled) return;

      const item = button.closest("[data-buy-together-item]");
      if (!item) return;

      const price = Number(button.dataset.price || 0);
      const original = Number(button.dataset.originalPrice || price);
      const variantId = button.dataset.variantId;
      const optionLabel = button.dataset.optionLabel || "";
      const available = button.dataset.available === "true";

      item.dataset.price = String(price);
      item.dataset.originalPrice = String(original);
      if (variantId) item.dataset.variantId = variantId;
      item.dataset.available = available ? "true" : "false";

      this.updateItemImage(item, button.dataset.image);

      this.updateItemPriceNodes(item, price, original);

      const summary = this.findSummaryByProductId(item.dataset.productId);
      if (summary) {
        const summaryVariant = summary.querySelector(
          "[data-buy-together-summary-variant]"
        );
        if (summaryVariant) {
          summaryVariant.textContent = optionLabel === "Default Title" ? "" : optionLabel;
          summaryVariant.classList.toggle("hidden", optionLabel === "Default Title");
        }
      }

      this.updateVariantOptionStyles(item);
      this.updateTotal();
    }

    async onAdd() {
      if (!this.addButton || this.addState === "adding") return;

      const selected = this.getSelectedItems();
      const purchasableSelected = selected.filter(
        (item) => item.dataset.available === "true"
      );
      if (!purchasableSelected.length || purchasableSelected.length !== selected.length) {
        this.updateTotal();
        return;
      }

      this.setButtonState("adding");

      try {
        const itemsPayload = purchasableSelected.map((item) => ({
          id: Number(item.dataset.variantId),
          quantity: 1,
        }));
        const sections = this.getSectionsToRender().map(
          (section) => section.section
        );

        const fetchCfg = window.webvista?.fetchConfig("json", "POST") || {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        };
        fetchCfg.body = JSON.stringify({
          items: itemsPayload,
          sections,
          sections_url: window.location.pathname,
        });

        const response = await fetch("/cart/add.js", fetchCfg);

        if (!response.ok) {
          const errorData = await response.json();
          document.dispatchEvent(
            new CustomEvent(this.cartErrorEvent, {
              detail: { error: errorData },
            })
          );
          throw new Error("Failed to add bundle to cart");
        }

        const data = await response.json();
        if (data?.sections) {
          this.renderSections(data.sections);
        }
        await this.openCartDrawer(this.addButton);
        document.dispatchEvent(
          new CustomEvent(this.cartUpdateEvent, {
            detail: { variantId: Number(purchasableSelected[0].dataset.variantId) },
          })
        );

        this.setButtonState("added");

        if (this.addedTimer) {
          clearTimeout(this.addedTimer);
        }

        this.addedTimer = setTimeout(() => {
          this.setButtonState("idle");
        }, 1200);
      } catch (error) {
        this.setButtonState("idle");
      }
    }
  }

  customElements.define("buy-together", BuyTogether);
}
